import { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Wand2, Save, X, ChefHat, Download, Check, Lock, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { getPaidSession, isPaid } from "../lib/paidDownloads";

const CATEGORIES = ["Techniques", "Ingredients", "Tools", "Cuisines", "Cooking Terms"];
const POS = ["noun", "verb", "adjective", "adverb", "phrase"];
const RECIPE_CATS = ["Mains", "Sides", "Salads", "Pasta", "Breakfast", "Starters", "Sauces", "Desserts"];
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const emptyEntry = {
  term: "", pronunciation: "", part_of_speech: "noun", definition: "", example: "",
  category: "Techniques", image_url: "", closing_line: "", related_recipe_slugs: [],
};
const emptyRecipe = {
  title: "", subtitle: "", servings: "4", prep_time: "15 min", cook_time: "30 min",
  ingredients: [{ item: "", quantity: "" }],
  steps: [{ order: 1, text: "" }],
  closing_line: "", image_url: "", related_term_slug: "", category: "Mains", kitchenware: [],
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  return <KitchenDesk />;
}

function KitchenDesk() {
  const [entries, setEntries] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [letter, setLetter] = useState("A");
  const [expandedSlug, setExpandedSlug] = useState(null);
  const [editing, setEditing] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [voiceLoadingSlug, setVoiceLoadingSlug] = useState(null);

  const load = useCallback(async () => {
    const [e, r] = await Promise.all([api.listEntries(), api.listRecipes()]);
    setEntries(e);
    setRecipes(r);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Group entries by first letter — the alphabet index is driven off real data.
  const byLetter = useMemo(() => {
    const map = {};
    for (const e of entries) {
      const first = (e.term || "").trim().charAt(0).toUpperCase();
      if (!ALPHABET.includes(first)) continue;
      (map[first] ||= []).push(e);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => a.term.localeCompare(b.term));
    }
    return map;
  }, [entries]);

  const recipeBySlug = useMemo(() => {
    const map = {};
    for (const r of recipes) map[r.slug] = r;
    return map;
  }, [recipes]);

  const activeLetters = useMemo(() => new Set(Object.keys(byLetter)), [byLetter]);
  const rowsForLetter = byLetter[letter] || [];

  const [newEntry, setNewEntry] = useState(null); // when non-null, full-page form is open

  // --- Actions ---
  const startEdit = (type, item) => setEditing({ type, slug: item.slug, data: { ...item } });
  const startCreate = () => setNewEntry({
    term: "", pronunciation: "", part_of_speech: "noun", category: "Techniques",
    formal_definition: "", definition: "", example: "", closing_line: "", image_url: "",
    servings: "4", prep_time: "15 min", cook_time: "30 min", subtitle: "",
    ingredients: [{ item: "", quantity: "", _k: `k_${Date.now()}` }],
    steps: [{ order: 1, text: "", _k: `k_${Date.now()}_s` }],
    kitchenware: [],
    recipe_category: "Mains",
    recipe_closing_line: "",
    // Enrichment fields filled by the AI autofill and passed through publish.
    kitchenware_details: [],
    critical_points: [],
    wow_factor_tagline: "",
    prep_notes: "",
  });

  const generateImage = async (prompt) => {
    setGenLoading(true);
    try {
      const { image_url } = await api.generateImage(prompt);
      setEditing((prev) => ({ ...prev, data: { ...prev.data, image_url } }));
      toast.success("Image generated");
    } catch (e) {
      toast.error("Image generation failed: " + (e.response?.data?.detail || e.message));
    } finally { setGenLoading(false); }
  };

  const save = async () => {
    try {
      const { type, slug, data } = editing;
      if (type === "entry") {
        if (slug) await api.updateEntry(slug, data);
        else await api.createEntry(data);
      } else {
        if (slug) await api.updateRecipe(slug, data);
        else await api.createRecipe(data);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const voiceRecipe = async (slug, term) => {
    setVoiceLoadingSlug(slug);
    try {
      const r = await api.generateRecipe(slug);
      toast.success(`Recipe "${r.title}" added for ${term}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recipe generation failed");
    } finally { setVoiceLoadingSlug(null); }
  };

  const downloadUrl = (slug) => `${BACKEND_URL}/api/glossary/${slug}/download`;

  return (    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="admin-page">
      <div className="mb-6">
        <div className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mb-3">Behind the pass</div>
        <h1 className="font-display text-5xl md:text-6xl">Kitchen Desk</h1>
      </div>

      {/* Hero image */}
      <div className="mb-8 border border-black/15 overflow-hidden">
        <img
          src="/kitchen-desk-hero.webp"
          alt="A warm home kitchen: hands rolling out dough on a wooden bench beside pantry jars, mixing bowls, eggs, herbs, tomatoes and a stand mixer in soft afternoon light."
          className="w-full h-auto object-cover"
          style={{ aspectRatio: "1994 / 610" }}
          data-testid="admin-kitchen-hero"
        />
      </div>

      {/* Alphabet strip */}
      <div className="mb-8" data-testid="admin-alphabet">
        <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">Pick a letter</div>
        <div className="flex flex-wrap gap-1.5">
          {ALPHABET.map((L) => {
            const active = L === letter;
            const has = activeLetters.has(L);
            return (
              <button
                key={L}
                onClick={() => { setLetter(L); setExpandedSlug(null); }}
                disabled={!has}
                data-testid={`admin-letter-${L}`}
                className="font-display text-xl md:text-2xl leading-none w-9 h-9 md:w-10 md:h-10 border transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                style={{
                  background: active ? "var(--tomato)" : "transparent",
                  color: active ? "#fff" : "var(--ink)",
                  borderColor: active ? "var(--tomato)" : "rgba(0,0,0,0.25)",
                }}
                title={has ? `${byLetter[L].length} term(s) starting with ${L}` : `No terms starting with ${L}`}
              >
                {L}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table header */}
      <div
        className="hidden md:grid grid-cols-12 gap-4 items-center px-4 py-3 border border-black/20 font-ui text-[10px] uppercase tracking-[0.25em]"
        style={{ background: "var(--mustard, #f0c96b)" }}
        data-testid="admin-table-header"
      >
        <div className="col-span-2">
          <button
            onClick={startCreate}
            className="btn-primary btn-accent text-xs"
            data-testid="admin-new-term"
          >
            <Plus size={14} /> New term
          </button>
        </div>
        <div className="col-span-4">Recipes / Terms</div>
        <div className="col-span-3">Kitchen table</div>
        <div className="col-span-3">Download full recipe + definition + kitchen table</div>
      </div>

      {/* Rows */}
      <div className="border-x border-b border-black/20" data-testid="admin-rows">
        {rowsForLetter.length === 0 && (
          <div className="px-6 py-10 text-center font-body opacity-60" data-testid="admin-empty">
            No terms yet under <strong className="font-display italic text-xl">{letter}</strong>. Click <em>New term</em> to add the first one.
          </div>
        )}

        {rowsForLetter.map((entry) => {
          const relatedSlug = (entry.related_recipe_slugs || [])[0];
          const recipe = relatedSlug ? recipeBySlug[relatedSlug] : null;
          const isExpanded = expandedSlug === entry.slug;
          const voiceBusy = voiceLoadingSlug === entry.slug;

          return (
            <div key={entry.slug} className="border-b border-black/10 last:border-b-0" data-testid={`admin-row-${entry.slug}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center px-4 py-3">
                <div className="md:col-span-2">
                  <img
                    src={entry.image_url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="w-16 h-16 md:w-20 md:h-20 object-cover border border-black/10"
                  />
                </div>

                <div className="md:col-span-4">
                  <div className="font-display text-2xl md:text-3xl leading-none">{entry.term}</div>
                  <div className="font-ui text-[10px] uppercase tracking-widest opacity-60 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{entry.category}</span>
                    {recipe && (
                      <span title={recipe.title}>· {recipe.title.length > 34 ? recipe.title.slice(0, 32) + "…" : recipe.title}</span>
                    )}
                  </div>
                </div>

                <div className="md:col-span-3">
                  {recipe ? (
                    <button
                      onClick={() => setExpandedSlug(isExpanded ? null : entry.slug)}
                      className="pill w-full justify-center"
                      style={{ background: isExpanded ? "var(--olive)" : "transparent", color: isExpanded ? "#fff" : "var(--ink)", borderColor: "var(--olive)" }}
                      data-testid={`admin-kitchen-table-${entry.slug}`}
                    >
                      <ChefHat size={12} /> {isExpanded ? "Close kitchen table" : "Open kitchen table"}
                    </button>
                  ) : (
                    <button
                      onClick={() => voiceRecipe(entry.slug, entry.term)}
                      disabled={voiceBusy}
                      className="pill w-full justify-center disabled:opacity-50"
                      style={{ background: "var(--tomato)", color: "#fff", borderColor: "var(--tomato)" }}
                      data-testid={`admin-voice-recipe-${entry.slug}`}
                    >
                      <ChefHat size={12} /> {voiceBusy ? "Cooking…" : "Voice a recipe"}
                    </button>
                  )}
                </div>

                <div className="md:col-span-3">
                  <PaywallDownloadPill slug={entry.slug} />
                </div>
              </div>

              {/* Expanded kitchen-table panel */}
              {isExpanded && recipe && (
                <KitchenTablePanel recipe={recipe} entry={entry} />
              )}
            </div>
          );
        })}
      </div>

      {/* Full-page NEW ENTRY form — creates a term + optional recipe + kitchenware in one save */}
      {newEntry && (
        <NewEntryFullPage
          data={newEntry}
          onChange={setNewEntry}
          onClose={() => setNewEntry(null)}
          onPublished={async (slug) => {
            setNewEntry(null);
            await load();
            // Jump the alphabet strip to the new term's letter and expand its panel
            const L = (slug || "").charAt(0).toUpperCase();
            if (L) setLetter(L);
            setExpandedSlug(slug);
            toast.success("Published to the site");
          }}
        />
      )}

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-[60] flex" data-testid="admin-editor">
          <div className="flex-1 bg-black/40" onClick={() => setEditing(null)} />
          <div className="w-full max-w-2xl bg-[var(--bg-primary)] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="font-display text-3xl">
                {editing.slug ? "Edit" : "New"} {editing.type === "entry" ? "term" : "recipe"}
              </div>
              <button onClick={() => setEditing(null)} className="pill" data-testid="admin-editor-close">
                <X size={14} />
              </button>
            </div>

            {editing.type === "entry" ? (
              <EntryForm data={editing.data} onChange={(data) => setEditing({ ...editing, data })} onGenerate={generateImage} genLoading={genLoading} recipes={recipes} />
            ) : (
              <RecipeForm data={editing.data} onChange={(data) => setEditing({ ...editing, data })} onGenerate={generateImage} genLoading={genLoading} entries={entries} />
            )}

            <div className="flex gap-3 mt-8">
              <button onClick={save} className="btn-primary" data-testid="admin-save">
                <Save size={16} /> Save
              </button>
              <button onClick={() => setEditing(null)} className="btn-primary btn-outline" data-testid="admin-cancel">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PaywallDownloadPill — the per-row download button on Kitchen Desk. Wraps
// the same $2.25 Stripe checkout flow used on the public /recipe/ page so
// no one gets a free PDF, even from behind the desk.
// ---------------------------------------------------------------------------
function PaywallDownloadPill({ slug }) {
  const [busy, setBusy] = useState(false);
  const paid = isPaid(slug);

  const start = async () => {
    if (busy) return;
    setBusy(true);
    if (paid) {
      const sid = getPaidSession(slug);
      const url = `${BACKEND_URL}/api/glossary/${slug}/download?session_id=${encodeURIComponent(sid)}`;
      try { (window.top || window).location.href = url; } catch { window.location.href = url; }
      setBusy(false);
      return;
    }
    try {
      const { checkout_url } = await api.createCheckoutSession(slug);
      // Stripe blocks iframe embedding — escape the Emergent preview pane.
      try { (window.top || window).location.href = checkout_url; }
      catch { window.location.href = checkout_url; }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Couldn't open Stripe checkout");
      setBusy(false);
    }
  };

  return (
    <button
      onClick={start}
      disabled={busy}
      className="pill w-full justify-center disabled:opacity-60"
      style={{ background: "var(--mustard)", borderColor: "rgba(0,0,0,0.5)" }}
      data-testid={`admin-download-${slug}`}
    >
      {busy ? <><Loader2 size={12} className="animate-spin" /> Opening…</> : paid ? <><Download size={12} /> Download recipe</> : <><Lock size={11} /> Unlock — $2.25</>}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Kitchen Table expand panel — the "full layout" a click reveals in-row.
// ---------------------------------------------------------------------------
function KitchenTablePanel({ recipe, entry }) {
  return (
    <div className="border-t border-dashed border-black/20 px-4 md:px-8 py-6 md:py-8" style={{ background: "rgba(240,201,107,0.12)" }} data-testid={`admin-kitchen-panel-${entry.slug}`}>
      <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">Kitchen table for</div>
      <h3 className="font-display text-3xl md:text-4xl leading-tight mb-1">{recipe.title}</h3>
      {recipe.subtitle && <p className="font-body italic opacity-70 mb-4">{recipe.subtitle}</p>}
      <div className="font-ui text-[10px] uppercase tracking-widest opacity-70 mb-6 flex gap-4 flex-wrap">
        <span>Serves {recipe.servings}</span><span>Prep {recipe.prep_time}</span><span>Cook {recipe.cook_time}</span>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-3">Ingredients</div>
          <ul className="space-y-2">
            {(recipe.ingredients || []).map((i, idx) => (
              <li key={idx} className="flex gap-3 border-b border-dashed border-black/15 pb-2">
                <span className="font-ui text-sm font-semibold w-24 shrink-0">{i.quantity}</span>
                <span className="font-body text-sm flex-1">{i.item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-3">Kitchenware you will need</div>
          <ul className="space-y-2">
            {(recipe.kitchenware || []).map((k, idx) => (
              <li key={idx} className="flex gap-3 border-b border-dashed border-black/15 pb-2">
                <span className="font-display italic text-lg leading-none pt-1 shrink-0" style={{ color: "var(--tomato)" }}>·</span>
                <span className="font-body text-sm flex-1">{k}</span>
              </li>
            ))}
            {(recipe.kitchenware || []).length === 0 && (
              <li className="font-body text-sm opacity-60 italic">No kitchenware list yet.</li>
            )}
          </ul>
        </div>
      </div>

      {recipe.steps?.length > 0 && (
        <div className="mt-8">
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-3">Method</div>
          <ol className="space-y-3">
            {recipe.steps.map((s, idx) => (
              <li key={idx} className="flex gap-4">
                <div className="font-display italic text-2xl leading-none pt-0.5 w-8 shrink-0" style={{ color: "var(--tomato)" }}>{s.order || idx + 1}</div>
                <div className="font-body text-sm md:text-base flex-1 leading-relaxed">{s.text}</div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Critical Points — The WOW Factor. Every recipe now carries this
          block (rule-based fallbacks fill in until the LLM enrichment
          script runs). Emerald border + tomato dots to match the recipe
          detail page and the printed PDF. */}
      {(recipe.critical_points?.length > 0 || recipe.wow_factor_tagline) && (
        <div
          className="mt-8 rounded-xl border-2 px-5 md:px-7 py-5 md:py-6"
          style={{ borderColor: "#087A46", background: "rgba(8,122,70,0.05)" }}
          data-testid={`admin-critical-wow-${entry.slug}`}
        >
          <div className="font-display text-2xl md:text-3xl leading-none mb-4" style={{ color: "#087A46" }}>
            Critical Points <span className="opacity-90 italic">— The WOW Factor</span>
          </div>
          {recipe.critical_points?.length > 0 && (
            <ul className="space-y-2.5">
              {recipe.critical_points.map((tip, idx) => (
                <li key={idx} className="flex gap-3">
                  <span className="font-display italic text-2xl leading-none pt-0.5 shrink-0" style={{ color: "var(--tomato)" }}>·</span>
                  <span className="font-body text-sm md:text-base leading-relaxed">{tip}</span>
                </li>
              ))}
            </ul>
          )}
          {recipe.wow_factor_tagline && (
            <p
              className="font-display italic text-lg md:text-xl mt-4 pt-4 border-t"
              style={{ color: "#087A46", borderColor: "rgba(8,122,70,0.25)" }}
              data-testid={`admin-wow-tagline-${entry.slug}`}
            >
              {recipe.wow_factor_tagline}
            </p>
          )}
        </div>
      )}

      {recipe.closing_line && (
        <p className="font-display italic text-2xl mt-6" style={{ color: "var(--olive)" }}>— {recipe.closing_line}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor drawer subcomponents (unchanged shape from prior version)
// ---------------------------------------------------------------------------
function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <div className="font-ui text-[10px] uppercase tracking-[0.25em] opacity-60 mb-2">{label}</div>
      {children}
    </label>
  );
}

const inputCls = "w-full border border-black/20 bg-white px-3 py-2 font-body rounded focus:outline-none focus:border-black";

function EntryForm({ data, onChange, onGenerate, genLoading, recipes }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div>
      <Field label="Term"><input className={inputCls} value={data.term} onChange={(e) => set("term", e.target.value)} data-testid="editor-term" /></Field>
      <Field label="Pronunciation"><input className={inputCls} value={data.pronunciation || ""} onChange={(e) => set("pronunciation", e.target.value)} data-testid="editor-pronunciation" /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Part of speech">
          <select className={inputCls} value={data.part_of_speech} onChange={(e) => set("part_of_speech", e.target.value)} data-testid="editor-pos">
            {POS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Category">
          <select className={inputCls} value={data.category} onChange={(e) => set("category", e.target.value)} data-testid="editor-category">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Definition"><textarea rows={4} className={inputCls} value={data.definition} onChange={(e) => set("definition", e.target.value)} data-testid="editor-definition" /></Field>
      <Field label="Example"><textarea rows={2} className={inputCls} value={data.example} onChange={(e) => set("example", e.target.value)} data-testid="editor-example" /></Field>
      <Field label="Closing line (witty)"><input className={inputCls} value={data.closing_line} onChange={(e) => set("closing_line", e.target.value)} data-testid="editor-closing" /></Field>
      <ImageBlock image_url={data.image_url} onChange={(v) => set("image_url", v)} onGenerate={() => onGenerate(`${data.term}, ${(data.definition || "").slice(0, 120)}`)} genLoading={genLoading} />
      <Field label="Related recipes">
        <select multiple className={inputCls} value={data.related_recipe_slugs || []} onChange={(e) => set("related_recipe_slugs", Array.from(e.target.selectedOptions).map((o) => o.value))} data-testid="editor-related-recipes">
          {recipes.map((r) => <option key={r.slug} value={r.slug}>{r.title}</option>)}
        </select>
      </Field>
    </div>
  );
}

function RecipeForm({ data, onChange, onGenerate, genLoading, entries }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const nextKey = () => `k_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const ingredients = (data.ingredients || []).map((i) => (i._k ? i : { ...i, _k: nextKey() }));
  const steps = (data.steps || []).map((s) => (s._k ? s : { ...s, _k: nextKey() }));
  const updateIng = (i, patch) => {
    const list = [...ingredients];
    list[i] = { ...list[i], ...patch };
    set("ingredients", list);
  };
  const addIng = () => set("ingredients", [...ingredients, { item: "", quantity: "", _k: nextKey() }]);
  const removeIng = (i) => set("ingredients", ingredients.filter((_, idx) => idx !== i));
  const updateStep = (i, text) => {
    const list = [...steps];
    list[i] = { ...list[i], text };
    set("steps", list);
  };
  const addStep = () => set("steps", [...steps, { order: steps.length + 1, text: "", _k: nextKey() }]);
  const removeStep = (i) => set("steps", steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx + 1 })));

  return (
    <div>
      <Field label="Title"><input className={inputCls} value={data.title} onChange={(e) => set("title", e.target.value)} data-testid="editor-title" /></Field>
      <Field label="Subtitle"><input className={inputCls} value={data.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} data-testid="editor-subtitle" /></Field>
      <div className="grid grid-cols-3 gap-4">
        <Field label="Servings"><input className={inputCls} value={data.servings} onChange={(e) => set("servings", e.target.value)} data-testid="editor-servings" /></Field>
        <Field label="Prep time"><input className={inputCls} value={data.prep_time} onChange={(e) => set("prep_time", e.target.value)} data-testid="editor-prep" /></Field>
        <Field label="Cook time"><input className={inputCls} value={data.cook_time} onChange={(e) => set("cook_time", e.target.value)} data-testid="editor-cook" /></Field>
      </div>
      <Field label="Category">
        <select className={inputCls} value={data.category} onChange={(e) => set("category", e.target.value)} data-testid="editor-recipe-category">
          {RECIPE_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Related glossary term">
        <select className={inputCls} value={data.related_term_slug || ""} onChange={(e) => set("related_term_slug", e.target.value)} data-testid="editor-related-term">
          <option value="">— none —</option>
          {entries.map((en) => <option key={en.slug} value={en.slug}>{en.term}</option>)}
        </select>
      </Field>

      <div className="mt-6 mb-4">
        <div className="font-ui text-[10px] uppercase tracking-[0.25em] opacity-60 mb-2">Ingredients</div>
        {ingredients.map((ing, i) => (
          <div key={ing._k} className="flex gap-2 mb-2" data-testid={`editor-ingredient-row-${i}`}>
            <input className={inputCls + " w-32"} placeholder="Qty" value={ing.quantity || ""} onChange={(e) => updateIng(i, { quantity: e.target.value })} data-testid={`editor-ingredient-quantity-${i}`} />
            <input className={inputCls} placeholder="Ingredient" value={ing.item} onChange={(e) => updateIng(i, { item: e.target.value })} data-testid={`editor-ingredient-item-${i}`} />
            <button onClick={() => removeIng(i)} className="pill" data-testid={`editor-ingredient-remove-${i}`}><X size={12} /></button>
          </div>
        ))}
        <button onClick={addIng} className="pill mt-2" data-testid="editor-add-ingredient"><Plus size={12} /> Add ingredient</button>
      </div>

      <div className="mt-6 mb-4">
        <div className="font-ui text-[10px] uppercase tracking-[0.25em] opacity-60 mb-2">Kitchenware you will need</div>
        <textarea
          rows={6}
          className={inputCls}
          placeholder="One item per line — e.g.&#10;Large mixing bowl&#10;Whisk or electric hand mixer&#10;Non-stick frying pan"
          value={(data.kitchenware || []).join("\n")}
          onChange={(e) => set("kitchenware", e.target.value.split("\n").map((l) => l.trim()).filter(Boolean))}
          data-testid="editor-kitchenware"
        />
        <div className="font-ui text-[10px] opacity-50 mt-2">One item per line. Beginner-friendly. No ingredients here.</div>
      </div>

      <div className="mt-6 mb-4">
        <div className="font-ui text-[10px] uppercase tracking-[0.25em] opacity-60 mb-2">Steps</div>
        {steps.map((s, i) => (
          <div key={s._k} className="flex gap-2 mb-2 items-start" data-testid={`editor-step-row-${i}`}>
            <div className="font-display italic text-2xl w-8 pt-1" style={{ color: "var(--tomato)" }}>{s.order}</div>
            <textarea rows={2} className={inputCls} value={s.text} onChange={(e) => updateStep(i, e.target.value)} data-testid={`editor-step-text-${i}`} />
            <button onClick={() => removeStep(i)} className="pill" data-testid={`editor-step-remove-${i}`}><X size={12} /></button>
          </div>
        ))}
        <button onClick={addStep} className="pill mt-2" data-testid="editor-add-step"><Plus size={12} /> Add step</button>
      </div>

      <Field label="Closing line (witty)"><input className={inputCls} value={data.closing_line} onChange={(e) => set("closing_line", e.target.value)} data-testid="editor-recipe-closing" /></Field>
      <ImageBlock image_url={data.image_url} onChange={(v) => set("image_url", v)} onGenerate={() => onGenerate(`${data.title}, plated dish, ${data.subtitle || ""}`)} genLoading={genLoading} />
    </div>
  );
}

function ImageBlock({ image_url, onChange, onGenerate, genLoading }) {
  const isDataUrl = typeof image_url === "string" && image_url.startsWith("data:");
  return (
    <Field label="Image">
      <div className="flex gap-3 items-start">
        <div className="w-32 h-32 shrink-0 border border-black/20 overflow-hidden bg-white">
          {image_url ? <img src={image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center font-ui text-xs opacity-50">No image</div>}
        </div>
        <div className="flex-1">
          {isDataUrl ? (
            <div className="border border-black/20 bg-white px-3 py-2 font-ui text-xs uppercase tracking-widest opacity-80 flex items-center gap-2 rounded">
              <Check size={14} style={{ color: "var(--olive)" }} /> Custom image ready
              <button
                type="button"
                onClick={() => onChange("")}
                className="pill ml-auto"
                style={{ padding: "2px 8px", fontSize: "10px" }}
              >
                Clear
              </button>
            </div>
          ) : (
            <input className={inputCls} placeholder="Paste image URL, or click Generate below" value={image_url || ""} onChange={(e) => onChange(e.target.value)} data-testid="editor-image-url" />
          )}
          <button onClick={onGenerate} disabled={genLoading} className="btn-primary btn-accent mt-2 disabled:opacity-50" data-testid="editor-generate-image">
            <Wand2 size={14} /> {genLoading ? "Generating…" : (image_url ? "Regenerate with AI" : "Generate with AI")}
          </button>
        </div>
      </div>
    </Field>
  );
}

// ---------------------------------------------------------------------------
// NewEntryFullPage — the immersive publish form.
// One page, no dim backdrop, no drawer. Captures term + recipe + kitchenware
// so a viewer can publish an entry that becomes part of the site forever.
// ---------------------------------------------------------------------------
function NewEntryFullPage({ data, onChange, onClose, onPublished }) {
  const [saving, setSaving] = useState(false);
  const [genImg, setGenImg] = useState(false);
  const [autofilling, setAutofilling] = useState(false);

  const set = (k, v) => onChange({ ...data, [k]: v });
  const nextKey = () => `k_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  /**
   * autofillEverything — the "type a title, fill everything" one-click flow.
   * Sends the current title to /api/kitchen-desk/autofill, which runs Claude
   * Sonnet to write the entire entry+recipe payload (plus a Nano-Banana cover
   * image) and drops it straight into the form. The user just reviews and
   * clicks Publish.
   */
  const autofillEverything = async () => {
    const title = (data.term || "").trim();
    if (!title) {
      toast.error("Type a recipe title first — e.g. 'Beef with Black Bean Sauce'");
      return;
    }
    setAutofilling(true);
    const t = toast.loading("Writing the whole entry with AI… (30–60 sec)");
    try {
      const filled = await api.autofillKitchenDesk(title);
      // Merge into the form, preserving keys for React list rendering.
      onChange({
        ...data,
        term: filled.term || data.term,
        pronunciation: filled.pronunciation || "",
        part_of_speech: filled.part_of_speech || "noun",
        category: filled.category || "Dishes",
        recipe_category: filled.recipe_category || "Mains",
        formal_definition: filled.formal_definition || "",
        definition: filled.definition || "",
        example: filled.example || "",
        closing_line: filled.closing_line || "",
        subtitle: filled.subtitle || "",
        servings: filled.servings || "4",
        prep_time: filled.prep_time || "15 min",
        cook_time: filled.cook_time || "30 min",
        ingredients: (filled.ingredients || []).map((i) => ({ ...i, _k: nextKey() })),
        steps: (filled.steps || []).map((s, idx) => ({ order: idx + 1, text: s.text || s, _k: nextKey() })),
        kitchenware: filled.kitchenware || [],
        kitchenware_details: filled.kitchenware_details || [],
        critical_points: filled.critical_points || [],
        wow_factor_tagline: filled.wow_factor_tagline || "",
        prep_notes: filled.prep_notes || "",
        recipe_closing_line: filled.recipe_closing_line || "",
        image_url: filled.image_url || data.image_url || "",
      });
      toast.success("Filled! Review and hit Save & Publish.", { id: t });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Autofill failed";
      toast.error(detail, { id: t });
    } finally {
      setAutofilling(false);
    }
  };

  // Ingredients
  const updateIng = (i, patch) => {
    const list = [...data.ingredients];
    list[i] = { ...list[i], ...patch };
    set("ingredients", list);
  };
  const addIng = () => set("ingredients", [...data.ingredients, { item: "", quantity: "", _k: nextKey() }]);
  const removeIng = (i) => set("ingredients", data.ingredients.filter((_, idx) => idx !== i));

  // Steps
  const updateStep = (i, text) => {
    const list = [...data.steps];
    list[i] = { ...list[i], text };
    set("steps", list);
  };
  const addStep = () => set("steps", [...data.steps, { order: (data.steps.length || 0) + 1, text: "", _k: nextKey() }]);
  const removeStep = (i) => set(
    "steps",
    data.steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx + 1 })),
  );

  const generateImage = async () => {
    const prompt = data.term
      ? `${data.term} — beautifully plated dish, editorial food photography, warm natural light, wooden bench`
      : "warm editorial food photograph on a wooden bench, natural light";
    setGenImg(true);
    try {
      const { image_url } = await api.generateImage(prompt);
      onChange({ ...data, image_url });
      toast.success("Image generated");
    } catch (e) {
      toast.error("Image generation failed: " + (e.response?.data?.detail || e.message));
    } finally { setGenImg(false); }
  };

  const publish = async () => {
    if (!data.term.trim()) { toast.error("Give it a name first"); return; }
    if (!data.definition.trim()) { toast.error("Add a plain-English definition"); return; }
    setSaving(true);
    try {
      // Clean payload — strip internal _k keys, drop empty ingredient/step rows.
      const payload = {
        ...data,
        ingredients: (data.ingredients || [])
          .filter((i) => (i.item || "").trim())
          .map(({ _k, ...rest }) => rest),
        steps: (data.steps || [])
          .filter((s) => (s.text || "").trim())
          .map(({ _k, ...rest }, idx) => ({ ...rest, order: idx + 1 })),
        kitchenware: (data.kitchenware || []).map((k) => k.trim()).filter(Boolean),
        kitchenware_details: data.kitchenware_details || [],
        critical_points: data.critical_points || [],
        wow_factor_tagline: data.wow_factor_tagline || "",
        prep_notes: data.prep_notes || "",
      };
      const res = await api.publishKitchenDesk(payload);
      onPublished(res.entry?.slug || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Publish failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-[var(--bg-primary)]" data-testid="new-entry-fullpage">
      {/* Top bar */}
      <div className="sticky top-0 z-10 border-b border-black/15 backdrop-blur bg-[var(--bg-primary)]/95">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center gap-4">
          <div className="flex-1">
            <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60">Publish to the site</div>
            <div className="font-display italic text-3xl md:text-4xl leading-none">New Kitchen Desk entry</div>
          </div>
          <button
            onClick={publish}
            disabled={saving}
            className="btn-primary btn-accent disabled:opacity-50"
            data-testid="new-entry-save"
          >
            <Save size={16} /> {saving ? "Publishing…" : "Save & Publish"}
          </button>
          <button onClick={onClose} className="pill" data-testid="new-entry-close" title="Close without saving">
            <X size={14} /> Cancel
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 md:py-12 space-y-10">
        {/* One-click AI autofill — reads the current title, asks Claude to
            write the whole entry + recipe payload, and drops it into every
            form field below. The user just reviews and hits Save & Publish. */}
        <div
          className="rounded-2xl border-2 px-6 md:px-8 py-5 md:py-6 flex flex-col md:flex-row md:items-center gap-4"
          style={{ borderColor: "#087A46", background: "rgba(8,122,70,0.06)" }}
          data-testid="new-entry-autofill-panel"
        >
          <div className="flex-1">
            <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-70 mb-1" style={{ color: "#087A46" }}>
              One-click magic
            </div>
            <div className="font-display italic text-2xl md:text-3xl leading-tight">
              Type a title above, then let AI fill in everything else.
            </div>
            <div className="font-body text-sm opacity-70 mt-1">
              Definition, ingredients, method, kitchenware, critical points, WOW factor and the cover photo — in about a minute.
            </div>
          </div>
          <button
            onClick={autofillEverything}
            disabled={autofilling}
            className="btn-primary btn-accent disabled:opacity-60 shrink-0"
            style={{ minWidth: "220px", justifyContent: "center" }}
            data-testid="new-entry-autofill-btn"
          >
            <Wand2 size={16} /> {autofilling ? "Filling everything…" : "Fill everything with AI"}
          </button>
        </div>

        {/* Hero: image + basic identity */}
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] border border-black/20 bg-white overflow-hidden" data-testid="new-entry-image-preview">
              {data.image_url ? (
                <img src={data.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center font-ui text-xs uppercase tracking-widest opacity-50 text-center px-6">
                  No image yet — click <em>Generate with AI</em> or paste a URL
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={generateImage}
                disabled={genImg}
                className="btn-primary btn-accent disabled:opacity-50 flex-1"
                data-testid="new-entry-generate-image"
              >
                <Wand2 size={14} /> {genImg ? "Generating…" : (data.image_url ? "Regenerate with AI" : "Generate with AI")}
              </button>
            </div>
            {!data.image_url?.startsWith("data:") && (
              <input
                className={inputCls + " mt-2 text-xs"}
                placeholder="…or paste an image URL"
                value={data.image_url || ""}
                onChange={(e) => set("image_url", e.target.value)}
                data-testid="new-entry-image-url"
              />
            )}
          </div>

          <div className="md:col-span-7 space-y-4">
            <div>
              <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">The name</div>
              <input
                className={inputCls + " font-display italic text-4xl md:text-5xl px-3 py-2 leading-tight"}
                placeholder="e.g. Greek Lemon Chicken with Potatoes"
                value={data.term}
                onChange={(e) => set("term", e.target.value)}
                data-testid="new-entry-term"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Pronunciation (optional)">
                <input className={inputCls} placeholder="e.g. sook-oh-toh-me-YO" value={data.pronunciation} onChange={(e) => set("pronunciation", e.target.value)} data-testid="new-entry-pronunciation" />
              </Field>
              <Field label="Category">
                <select className={inputCls} value={data.category} onChange={(e) => set("category", e.target.value)} data-testid="new-entry-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Part of speech">
                <select className={inputCls} value={data.part_of_speech} onChange={(e) => set("part_of_speech", e.target.value)} data-testid="new-entry-pos">
                  {POS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Recipe category">
                <select className={inputCls} value={data.recipe_category} onChange={(e) => set("recipe_category", e.target.value)} data-testid="new-entry-recipe-category">
                  {RECIPE_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
          </div>
        </div>

        {/* Definition block */}
        <section>
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-3">Definition</div>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Formal definition (optional)">
              <textarea rows={3} className={inputCls} placeholder="Dictionary-style. Keep it tight." value={data.formal_definition} onChange={(e) => set("formal_definition", e.target.value)} data-testid="new-entry-formal-definition" />
            </Field>
            <Field label="In plain English">
              <textarea rows={3} className={inputCls} placeholder="How you'd actually describe it to a friend." value={data.definition} onChange={(e) => set("definition", e.target.value)} data-testid="new-entry-definition" />
            </Field>
          </div>
          <Field label="In a sentence (example)">
            <textarea rows={2} className={inputCls} placeholder="Show it in use." value={data.example} onChange={(e) => set("example", e.target.value)} data-testid="new-entry-example" />
          </Field>
          <Field label="Closing line for the definition (witty, one-liner)">
            <input className={inputCls} value={data.closing_line} onChange={(e) => set("closing_line", e.target.value)} data-testid="new-entry-closing" />
          </Field>
        </section>

        {/* Recipe block */}
        <section className="border-t border-black/15 pt-8">
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-3">The recipe (optional — leave blank if this is glossary-only)</div>
          <Field label="Subtitle">
            <input className={inputCls} placeholder="A short flavour headline" value={data.subtitle} onChange={(e) => set("subtitle", e.target.value)} data-testid="new-entry-subtitle" />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Servings"><input className={inputCls} value={data.servings} onChange={(e) => set("servings", e.target.value)} data-testid="new-entry-servings" /></Field>
            <Field label="Prep time"><input className={inputCls} value={data.prep_time} onChange={(e) => set("prep_time", e.target.value)} data-testid="new-entry-prep" /></Field>
            <Field label="Cook time"><input className={inputCls} value={data.cook_time} onChange={(e) => set("cook_time", e.target.value)} data-testid="new-entry-cook" /></Field>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-6">
            <div>
              <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">Ingredients</div>
              {(data.ingredients || []).map((ing, i) => (
                <div key={ing._k || i} className="flex gap-2 mb-2" data-testid={`new-entry-ing-row-${i}`}>
                  <input className={inputCls + " w-32"} placeholder="Qty (e.g. 250 g)" value={ing.quantity || ""} onChange={(e) => updateIng(i, { quantity: e.target.value })} data-testid={`new-entry-ing-qty-${i}`} />
                  <input className={inputCls} placeholder="Ingredient" value={ing.item} onChange={(e) => updateIng(i, { item: e.target.value })} data-testid={`new-entry-ing-item-${i}`} />
                  <button onClick={() => removeIng(i)} className="pill" data-testid={`new-entry-ing-remove-${i}`}><X size={12} /></button>
                </div>
              ))}
              <button onClick={addIng} className="pill" data-testid="new-entry-ing-add"><Plus size={12} /> Add ingredient</button>
            </div>

            <div>
              <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">Kitchenware you will need</div>
              <textarea
                rows={10}
                className={inputCls}
                placeholder="One item per line — e.g.&#10;Large roasting tray&#10;Sharp knife&#10;Measuring jug&#10;Meat thermometer"
                value={(data.kitchenware || []).join("\n")}
                onChange={(e) => set("kitchenware", e.target.value.split("\n"))}
                data-testid="new-entry-kitchenware"
              />
              <div className="font-ui text-[10px] opacity-50 mt-2">
                Leave this blank and we&#39;ll auto-generate a beginner-friendly list from your ingredients &amp; method on save.
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">Method</div>
            {(data.steps || []).map((s, i) => (
              <div key={s._k || i} className="flex gap-3 mb-3 items-start" data-testid={`new-entry-step-row-${i}`}>
                <div className="font-display italic text-3xl leading-none w-10 pt-1 shrink-0" style={{ color: "var(--tomato)" }}>{s.order || i + 1}</div>
                <textarea rows={2} className={inputCls} placeholder="Describe this step in plain English." value={s.text} onChange={(e) => updateStep(i, e.target.value)} data-testid={`new-entry-step-text-${i}`} />
                <button onClick={() => removeStep(i)} className="pill" data-testid={`new-entry-step-remove-${i}`}><X size={12} /></button>
              </div>
            ))}
            <button onClick={addStep} className="pill" data-testid="new-entry-step-add"><Plus size={12} /> Add step</button>
          </div>

          <Field label="Closing line for the recipe (witty)">
            <input className={inputCls} value={data.recipe_closing_line} onChange={(e) => set("recipe_closing_line", e.target.value)} data-testid="new-entry-recipe-closing" />
          </Field>
        </section>

        <div className="flex gap-3 pt-6 border-t border-black/15">
          <button
            onClick={publish}
            disabled={saving}
            className="btn-primary btn-accent disabled:opacity-50"
            data-testid="new-entry-save-bottom"
          >
            <Save size={16} /> {saving ? "Publishing…" : "Save & Publish"}
          </button>
          <button onClick={onClose} className="btn-primary btn-outline" data-testid="new-entry-cancel-bottom">
            Cancel
          </button>
          <div className="font-ui text-[10px] uppercase tracking-widest opacity-50 self-center">
            After saving, this entry becomes part of the site forever — every future visitor will see it.
          </div>
        </div>
      </div>
    </div>
  );
}
